import {
  Box,
  Flex,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { forwardRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  GRADES,
  MAX_GRADE,
  gradeLabel,
  gradeValueText,
  useGradeKeys,
} from "../../../../common/award-criteria/grade";
import { ReadonlyFieldLabel } from "../../../sourcing-events/bid-readonly/ReadonlyAnswerCard";

// Wide enough that every cell is an easy target, narrow enough that the strip does not run the width
// of the screen. The cells share what is there, so it degrades rather than wraps.
const MAX_WIDTH = "34rem";

const DASH = "–";

const THUMB_SIZE = "9";

const SIZES = {
  md: { maxW: MAX_WIDTH, cell: "9", height: "8", fontSize: "sm" },
  sm: { maxW: undefined, cell: "7", height: "7", fontSize: "xs" },
} as const;

interface Props {
  value: number | null;
  onChange: (value: number | null) => void;
  isReadOnly?: boolean;
  onDone?: () => void;
  size?: keyof typeof SIZES;
}

/**
 * The buyer's grade for one criterion: eleven cells from 0 to 10, filled green up to and including
 * the one chosen, so the level can be read at a glance rather than parsed from a number.
 *
 * The strip is a single tab stop carrying slider semantics — the cells are decoration for the mouse.
 * Arrow keys step, Home/End jump to the ends, and the digits set a grade outright.
 */
export const ScoreScale = forwardRef<HTMLDivElement, Props>(function ScoreScale(
  { value, onChange, isReadOnly = false, onDone, size = "md" },
  ref,
) {
  const { t } = useTranslation();
  const sizing = SIZES[size];
  const onPhone = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  const { select, onKeyDown } = useGradeKeys({ value, onChange, isReadOnly, stepping: !onPhone });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onDone?.();
      return;
    }
    onKeyDown(event);
  };

  const label = value != null ? gradeLabel(t, value) : null;
  const valueText = gradeValueText(t, value);

  return (
    <Box>
      <Flex align="baseline" justify="space-between" gap="3" maxW={sizing.maxW} mb="1.5">
        <ReadonlyFieldLabel>{t("Grade")}</ReadonlyFieldLabel>
        <Text fontSize="sm" color="smMuted">
          {value != null ? (
            <>
              <Text as="span" fontWeight="bold" color="smText">
                {value}
              </Text>{" "}
              {t("of {{max}} - {{label}}", { max: MAX_GRADE, label })}
            </>
          ) : (
            t("Not given")
          )}
        </Text>
      </Flex>
      {onPhone ? (
        <GradeSlider
          ref={ref}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          isReadOnly={isReadOnly}
          valueText={valueText}
          onDone={onDone}
        />
      ) : (
      <HStack
        ref={ref}
        spacing="1"
        maxW={sizing.maxW}
        role="slider"
        tabIndex={0}
        aria-label={String(t("Grade"))}
        aria-valuemin={0}
        aria-valuemax={MAX_GRADE}
        aria-valuenow={value ?? undefined}
        aria-valuetext={valueText}
        aria-readonly={isReadOnly || undefined}
        onKeyDown={handleKeyDown}
        // Cell clicks bubble to here, so picking a grade with the mouse also lands focus on the
        // strip — the arrow keys carry on from where the click left off.
        onClick={(event) => event.currentTarget.focus()}
        // Framed in the same grey as the justification field below, with air between the frame and
        // the cells so it reads as one control rather than a lasso around eleven. Only the colour
        // changes on focus, so nothing shifts under the cursor.
        border="1px solid"
        borderColor="smBorder"
        borderRadius="lg"
        p="1.5"
        outline="none"
        // Softer than the cell hover on purpose: the frame says "you are in the control", the cell
        // under the cursor says which grade you are about to give. Two equal signals compete.
        _hover={isReadOnly ? undefined : { borderColor: "smBackgroundTertiary" }}
        _focusVisible={{ borderColor: "smSuccess" }}
      >
        {GRADES.map((score) => (
          <GradeCell
            key={score}
            score={score}
            selected={value === score}
            // Everything up to the chosen grade is tinted, which is what turns eleven buttons into
            // one bar you read a level off.
            filled={value != null && score < value}
            isReadOnly={isReadOnly}
            sizing={sizing}
            onSelect={() => {
              select(score);
              onDone?.();
            }}
          />
        ))}
      </HStack>
      )}
    </Box>
  );
});

const GradeSlider = forwardRef<
  HTMLDivElement,
  {
    value: number | null;
    onChange: (value: number | null) => void;
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    isReadOnly: boolean;
    valueText: string;
    onDone?: () => void;
  }
>(function GradeSlider({ value, onChange, onKeyDown, isReadOnly, valueText, onDone }, ref) {
  const { t } = useTranslation();

  return (
    <Slider
      value={value ?? 0}
      min={0}
      max={MAX_GRADE}
      step={1}
      isReadOnly={isReadOnly}
      focusThumbOnChange={false}
      onChange={onChange}
      onChangeEnd={() => onDone?.()}
      onKeyDown={onKeyDown}
      aria-label={String(t("Grade"))}
      aria-valuetext={valueText}
      py="3"
      // ReadonlyAnswerCard clips what leaves it, and the thumb at 10 hangs past the track's end.
      pr={THUMB_SIZE}
    >
      <SliderTrack h="2.5" bg="smBackgroundSecondary" borderRadius="full">
        <SliderFilledTrack bg="smSuccess" />
      </SliderTrack>
      {GRADES.slice(1, MAX_GRADE).map((grade) => (
        <SliderMark key={grade} value={grade}>
          <Box w="1px" h="2.5" bg="smBackground" opacity={0.9} mt="-1.25" />
        </SliderMark>
      ))}
      <SliderThumb
        ref={ref}
        boxSize={THUMB_SIZE}
        borderWidth="2px"
        borderColor="smSuccess"
        bg="smBackground"
        color="smSuccess"
        fontWeight="bold"
        fontSize="sm"
      >
        {value ?? DASH}
      </SliderThumb>
    </Slider>
  );
});

const GradeCell = ({
  score,
  selected,
  filled,
  isReadOnly,
  onSelect,
  sizing,
}: {
  score: number;
  selected: boolean;
  filled: boolean;
  isReadOnly: boolean;
  onSelect: () => void;
  sizing: (typeof SIZES)[keyof typeof SIZES];
}) => (
  <Box
    role="presentation"
    data-testid={`grade-${score}`}
    data-selected={selected ? "true" : undefined}
    onClick={isReadOnly ? undefined : onSelect}
    flex="1"
    minW={sizing.cell}
    h={sizing.height}
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderRadius="md"
    fontSize={sizing.fontSize}
    fontWeight={selected ? "bold" : "medium"}
    cursor={isReadOnly ? "default" : "pointer"}
    bg={selected ? "smSuccess" : filled ? "smSuccessSoft" : "smBackgroundSecondary"}
    color={selected ? "white" : filled ? "smSuccess" : "smMuted"}
    // A hairline so eleven cells read as eleven choices rather than one band. The chosen cell takes
    // its own fill as its border and stays a solid block.
    border="1px solid"
    borderColor={selected ? "smSuccess" : filled ? "smSuccessSoftBorder" : "smBorder"}
    // The cursor deepens the border rather than the fill. Chakra's green has no step between 50 and
    // 100, so a fill hover is either invisible or three times louder than the grey cells' — the
    // border has a shade at the right weight on both sides, and needs no colour inventing. The two
    // accent tokens are the same step on their respective scales, so neither half shouts.
    _hover={
      isReadOnly || selected
        ? undefined
        : { borderColor: filled ? "smStatusOkAccent" : "smStatusNeutralAccent" }
    }
  >
    {score}
  </Box>
);
